export type Rating = 'again' | 'hard' | 'good' | 'easy';
export type CardStatus = 'new' | 'learning' | 'review' | 'relearning';

export interface Sm2Config {
  learningStepsMinutes: number[];
  graduatingIntervalDays: number;
  easyIntervalDays: number;
  easyBonus: number;
  minimumEase: number;
  defaultEase: number;
}

export const DEFAULT_SM2_CONFIG: Sm2Config = {
  learningStepsMinutes: [1, 10, 1440],
  graduatingIntervalDays: 1,
  easyIntervalDays: 4,
  easyBonus: 1.3,
  minimumEase: 1.3,
  defaultEase: 2.5,
};

export interface ScheduleInput {
  status: CardStatus;
  easeFactor: number | null;
  intervalDays: number;
  learningStep: number;
  rating: Rating;
  config: Sm2Config;
  now: Date;
}

export interface ScheduleOutput {
  status: CardStatus;
  easeFactor: number | null;
  intervalDays: number;
  learningStep: number;
  dueAt: Date;
  lapsesDelta: number;
  repsDelta: number;
}

export function clampEase(ease: number, minimumEase: number): number {
  return Math.max(minimumEase, Math.min(2.5, ease));
}

export function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60 * 1000);
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
}

export function parseFlashcardConfig(raw: unknown): Sm2Config & {
  newCardsPerDay: number;
  maxReviewsPerDay: number;
  allowStudentCreatedCards: boolean;
} {
  const base = {
    ...DEFAULT_SM2_CONFIG,
    newCardsPerDay: 20,
    maxReviewsPerDay: 200,
    allowStudentCreatedCards: true,
  };
  if (!raw || typeof raw !== 'object') return base;
  const c = raw as Record<string, unknown>;
  return {
    ...base,
    newCardsPerDay: typeof c.newCardsPerDay === 'number' ? c.newCardsPerDay : base.newCardsPerDay,
    maxReviewsPerDay: typeof c.maxReviewsPerDay === 'number' ? c.maxReviewsPerDay : base.maxReviewsPerDay,
    allowStudentCreatedCards:
      typeof c.allowStudentCreatedCards === 'boolean'
        ? c.allowStudentCreatedCards
        : base.allowStudentCreatedCards,
    learningStepsMinutes: Array.isArray(c.learningStepsMinutes)
      ? (c.learningStepsMinutes as number[])
      : base.learningStepsMinutes,
    graduatingIntervalDays:
      typeof c.graduatingIntervalDays === 'number' ? c.graduatingIntervalDays : base.graduatingIntervalDays,
    easyIntervalDays: typeof c.easyIntervalDays === 'number' ? c.easyIntervalDays : base.easyIntervalDays,
    easyBonus: typeof c.easyBonus === 'number' ? c.easyBonus : base.easyBonus,
    minimumEase: typeof c.minimumEase === 'number' ? c.minimumEase : base.minimumEase,
    defaultEase: typeof c.defaultEase === 'number' ? c.defaultEase : base.defaultEase,
  };
}

/** Priority for queue ordering (lower = sooner). */
export const STATUS_PRIORITY: Record<CardStatus, number> = {
  relearning: 0,
  learning: 1,
  new: 2,
  review: 3,
};

export function scheduleNext(input: ScheduleInput): ScheduleOutput {
  const effectiveStatus: CardStatus = input.status === 'new' ? 'learning' : input.status;
  const effectiveInput: ScheduleInput = {
    ...input,
    status: effectiveStatus,
    learningStep: input.status === 'new' ? 0 : input.learningStep,
    easeFactor: input.status === 'new' ? null : input.easeFactor,
    intervalDays: input.status === 'new' ? 0 : input.intervalDays,
  };

  if (effectiveStatus === 'learning') {
    return scheduleLearning(effectiveInput);
  }
  if (effectiveStatus === 'relearning') {
    return scheduleRelearning(effectiveInput);
  }
  return scheduleReview(effectiveInput);
}

function scheduleLearning(input: ScheduleInput): ScheduleOutput {
  const { rating, learningStep, config, now } = input;
  const steps = config.learningStepsMinutes;
  const lastIndex = steps.length - 1;

  if (rating === 'again') {
    return {
      status: 'learning',
      easeFactor: null,
      intervalDays: 0,
      learningStep: 0,
      dueAt: addMinutes(now, steps[0] ?? 1),
      lapsesDelta: 0,
      repsDelta: 0,
    };
  }

  if (rating === 'hard') {
    const idx = Math.min(learningStep, lastIndex);
    const minutes = steps[idx] ?? 1;
    return {
      status: 'learning',
      easeFactor: null,
      intervalDays: 0,
      learningStep,
      dueAt: addMinutes(now, Math.max(1, Math.floor(minutes * 1.2))),
      lapsesDelta: 0,
      repsDelta: 0,
    };
  }

  if (rating === 'easy') {
    return graduateToReview(config.easyIntervalDays, config.defaultEase, now, config);
  }

  // good
  if (learningStep >= lastIndex) {
    return graduateToReview(config.graduatingIntervalDays, config.defaultEase, now, config);
  }

  const nextStep = learningStep + 1;
  const minutes = steps[nextStep] ?? steps[lastIndex] ?? 1;
  return {
    status: 'learning',
    easeFactor: null,
    intervalDays: 0,
    learningStep: nextStep,
    dueAt: addMinutes(now, minutes),
    lapsesDelta: 0,
    repsDelta: 1,
  };
}

function graduateToReview(
  intervalDays: number,
  ease: number,
  now: Date,
  _config: Sm2Config
): ScheduleOutput {
  return {
    status: 'review',
    easeFactor: ease,
    intervalDays,
    learningStep: 0,
    dueAt: addDays(now, intervalDays),
    lapsesDelta: 0,
    repsDelta: 1,
  };
}

function scheduleRelearning(input: ScheduleInput): ScheduleOutput {
  const { rating, learningStep, easeFactor, intervalDays, config, now } = input;
  const steps = config.learningStepsMinutes;
  const lastIndex = steps.length - 1;
  const ease = easeFactor ?? config.defaultEase;

  if (rating === 'again') {
    return {
      status: 'relearning',
      easeFactor: clampEase(ease - 0.2, config.minimumEase),
      intervalDays: 1,
      learningStep: 0,
      dueAt: addMinutes(now, steps[0] ?? 1),
      lapsesDelta: 1,
      repsDelta: 0,
    };
  }

  if (rating === 'hard') {
    const idx = Math.min(learningStep, lastIndex);
    return {
      status: 'relearning',
      easeFactor: ease,
      intervalDays: 1,
      learningStep,
      dueAt: addMinutes(now, Math.max(1, Math.floor((steps[idx] ?? 1) * 1.2))),
      lapsesDelta: 0,
      repsDelta: 0,
    };
  }

  if (rating === 'easy' || learningStep >= lastIndex) {
    const restored = Math.max(config.graduatingIntervalDays, intervalDays);
    return {
      status: 'review',
      easeFactor: ease,
      intervalDays: restored,
      learningStep: 0,
      dueAt: addDays(now, restored),
      lapsesDelta: 0,
      repsDelta: 1,
    };
  }

  const nextStep = learningStep + 1;
  return {
    status: 'relearning',
    easeFactor: ease,
    intervalDays: 1,
    learningStep: nextStep,
    dueAt: addMinutes(now, steps[nextStep] ?? 10),
    lapsesDelta: 0,
    repsDelta: 1,
  };
}

function scheduleReview(input: ScheduleInput): ScheduleOutput {
  const { rating, easeFactor, intervalDays, config, now } = input;
  const ease = easeFactor ?? config.defaultEase;
  const interval = Math.max(0.01, intervalDays);

  if (rating === 'again') {
    return {
      status: 'relearning',
      easeFactor: clampEase(ease - 0.2, config.minimumEase),
      intervalDays: 1,
      learningStep: 0,
      dueAt: addMinutes(now, config.learningStepsMinutes[0] ?? 1),
      lapsesDelta: 1,
      repsDelta: 0,
    };
  }

  if (rating === 'hard') {
    const newEase = clampEase(ease - 0.15, config.minimumEase);
    const newInterval = Math.max(1, interval * 1.2);
    return {
      status: 'review',
      easeFactor: newEase,
      intervalDays: newInterval,
      learningStep: 0,
      dueAt: addDays(now, newInterval),
      lapsesDelta: 0,
      repsDelta: 1,
    };
  }

  if (rating === 'good') {
    const newInterval = Math.max(1, interval * ease);
    return {
      status: 'review',
      easeFactor: ease,
      intervalDays: newInterval,
      learningStep: 0,
      dueAt: addDays(now, newInterval),
      lapsesDelta: 0,
      repsDelta: 1,
    };
  }

  const newEase = clampEase(ease + 0.15, config.minimumEase);
  const newInterval = Math.max(1, interval * ease * config.easyBonus);
  return {
    status: 'review',
    easeFactor: newEase,
    intervalDays: newInterval,
    learningStep: 0,
    dueAt: addDays(now, newInterval),
    lapsesDelta: 0,
    repsDelta: 1,
  };
}
